import { useEffect, useState, useRef, useCallback } from "react";
import { Camera, Trash2, X, ImageIcon, Columns2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../api";
import { PhotoCompare } from "./PhotoCompare";

const PhotoImg = ({ id, className, onClick }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url = null;
    api.get(`/photos/${id}/image`, { responseType: "blob" }).then(({ data }) => {
      url = URL.createObjectURL(data);
      setSrc(url);
    }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [id]);
  if (!src) return <div className={`${className} bg-[#14141A] animate-pulse`} />;
  return <img src={src} alt="Progression" className={className} onClick={onClick} />;
};

export const PhotosSection = () => {
  const [photos, setPhotos] = useState([]);
  const [label, setLabel] = useState("Avant");
  const [uploading, setUploading] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const [sel, setSel] = useState([]);
  const [compare, setCompare] = useState(null);
  const inputRef = useRef(null);

  const toggleSelect = (p) => {
    const already = sel.find((s) => s.id === p.id);
    const next = already ? sel.filter((s) => s.id !== p.id) : [...sel, p];
    if (next.length === 2) {
      const [a, b] = [...next].sort((x, y) => (x.date === y.date ? 0 : x.date < y.date ? -1 : 1));
      setCompare({ a, b });
      setSel([]);
      setCompareMode(false);
    } else {
      setSel(next);
    }
  };

  const load = useCallback(() => api.get("/photos").then(({ data }) => setPhotos(data)), []);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("label", label);
      await api.post("/photos", form);
      toast.success("Photo ajoutée !");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/photos/${id}`);
    toast.success("Photo supprimée");
    setViewer(null);
    load();
  };

  return (
    <section data-testid="photos-section">
      <h2 className="font-display text-2xl text-white mb-2 flex items-center gap-2">
        PHOTOS DE PROGRESSION <ImageIcon size={18} className="text-[#FF3B30]" />
      </h2>
      {photos.length >= 2 && (
        <button data-testid="compare-mode-button" onClick={() => { setCompareMode(!compareMode); setSel([]); }}
          className={`mb-2 h-9 px-3 text-xs uppercase tracking-wider border flex items-center gap-2 transition-colors ${
            compareMode ? "border-[#FF3B30] text-white bg-[#FF3B30]/10" : "border-white/15 text-[#B0B0B4] hover:text-white"}`}>
          <Columns2 size={14} /> {compareMode ? "Choisis 2 photos à comparer..." : "Comparer avant / après"}
        </button>
      )}
      <div className="flex gap-2">
        <select data-testid="photo-label-select" value={label} onChange={(e) => setLabel(e.target.value)}
          className="h-11 bg-[#14141A] text-white text-sm px-3 border border-[#2E2E3A] focus:outline-none">
          <option>Avant</option>
          <option>Après</option>
          <option>Progression</option>
        </select>
        <button data-testid="add-photo-button" disabled={uploading} onClick={() => inputRef.current?.click()}
          className="flex-1 h-11 bg-[#FF3B30] text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-[#e0322a] transition-colors disabled:opacity-40">
          <Camera size={16} /> {uploading ? "Envoi..." : "Ajouter une photo"}
        </button>
        <input ref={inputRef} data-testid="photo-file-input" type="file" accept="image/*" capture="environment"
          className="hidden" onChange={upload} />
      </div>
      {photos.length === 0 ? (
        <p className="text-[#B0B0B4] text-xs mt-3">Prends une photo « Avant » aujourd'hui — tu me remercieras dans 8 semaines 💪</p>
      ) : (
        <div className="grid grid-cols-3 gap-1.5 mt-3">
          {photos.map((p) => (
            <div key={p.id} data-testid={`photo-${p.id}`}
              className={`relative cursor-pointer ${compareMode && sel.find((s) => s.id === p.id) ? "ring-2 ring-[#FF3B30]" : ""}`}
              onClick={() => (compareMode ? toggleSelect(p) : setViewer(p))}>
              <PhotoImg id={p.id} className="w-full aspect-[3/4] object-cover border border-[#2E2E3A]" />
              <div className="absolute bottom-0 left-0 right-0 bg-black/70 px-1.5 py-0.5">
                <p className="text-white text-[10px] font-medium">{p.label}</p>
                <p className="text-[#B0B0B4] text-[9px]">{p.date}</p>
              </div>
            </div>
          ))}
        </div>
      )}
      {compare && <PhotoCompare a={compare.a} b={compare.b} onClose={() => setCompare(null)} />}
      {viewer && (
        <div className="fixed inset-0 z-[70] bg-black/95 flex flex-col" data-testid="photo-viewer">
          <div className="flex items-center justify-between p-4">
            <div>
              <p className="text-white font-medium">{viewer.label}</p>
              <p className="text-[#B0B0B4] text-xs">{viewer.date}</p>
            </div>
            <div className="flex gap-2">
              <button data-testid="photo-delete-button" onClick={() => remove(viewer.id)}
                className="text-[#B0B0B4] hover:text-[#FF3B30] p-2 transition-colors"><Trash2 size={20} /></button>
              <button data-testid="photo-viewer-close" onClick={() => setViewer(null)}
                className="text-[#B0B0B4] hover:text-white p-2 transition-colors"><X size={22} /></button>
            </div>
          </div>
          <div className="flex-1 flex items-center justify-center p-4 overflow-hidden">
            <PhotoImg id={viewer.id} className="max-w-full max-h-full object-contain" />
          </div>
        </div>
      )}
    </section>
  );
};
